export interface PopUpAdComponentProps {
  /** Image URL from ad service — used on desktop, and everywhere when no mobile URL is given */
  imageUrl?: string;
  /** Optional narrow-screen crop of the same creative; falls back to `imageUrl` */
  mobileImageUrl?: string;
  /** Click URL for the ad */
  clickUrl?: string;
  /** Click handler */
  onClick?: () => void;
  /** Alt text for accessibility */
  altText?: string;
  /** Ad slot identifier for ad service */
  slotId?: string;
  /** Custom width (default: full width) */
  width?: number | string;
  /** Custom height (default: auto) */
  height?: number | string;
}

/**
 * PopUp Ad Component
 * Promotional image component for use in modals and popups
 */
export default function PopUpAdComponent({
  imageUrl,
  mobileImageUrl,
  clickUrl,
  onClick,
  altText = 'Promotional Advertisement',
  slotId = 'popup-ad',
  width = '100%',
  height = 'auto',
}: PopUpAdComponentProps) {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (clickUrl) {
      window.open(clickUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="px-4 pb-4">
      {imageUrl ? (
        /*
         * `<picture>` rather than a JS breakpoint: the browser resolves the media query
         * before it fetches, so exactly one file is downloaded and there is no first-paint
         * flash of the wrong crop. The `<img>` carries the MOBILE creative as its src and the
         * `<source>` the desktop one, which is what makes every single-asset case — and any
         * browser without `<picture>` — fall through to the desktop URL.
         */
        <picture>
          {mobileImageUrl && <source media="(min-width: 768px)" srcSet={imageUrl} />}
          <img
            src={mobileImageUrl || imageUrl}
            alt={altText}
            onClick={imageUrl || clickUrl ? handleClick : undefined}
            className={`w-full h-auto rounded-lg object-cover ${
              imageUrl || clickUrl ? 'cursor-pointer' : ''
            }`}
            style={{ width, height }}
            role="banner"
            aria-label={altText}
            data-ad-slot={slotId}
          />
        </picture>
      ) : (
        /* Placeholder for promotional image */
        <div
          className="w-full rounded-lg flex items-center justify-center border border-dashed border-gray-300"
          style={{
            width,
            height: height === 'auto' ? '144px' : height,
            backgroundColor: '#f3f4f6',
          }}
        >
          <span className="text-gray-400 text-sm font-medium">Promo Image</span>
        </div>
      )}
    </div>
  );
}
